import { useEffect, useState } from "react";
import {
  ApiError,
  PIN_MAX_LENGTH,
  api,
  passwordSignInError,
  pinSignInError,
  sendRetryDelayMs,
  signInWithPasskey,
  webauthnErrorMessage,
  webauthnSupported,
  type accountApi,
} from "@helm/core";
import { lazyComponent } from "../components/lazy";
import { Brand } from "../components/Brand";
import { useSecondsLeft } from "./useSecondsLeft";

/** A readable message for a failed magic-link request (SPEC §20 abuse limits answer 429). */
export function magicLinkError(e: unknown): string {
  if (e instanceof ApiError && e.status === 429) {
    return "Too many sign-in requests from this network or for this email. Wait up to an hour, then try again.";
  }
  return e instanceof Error ? e.message : "request failed";
}

function passkeyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 429) return "Too many passkey sign-in attempts from this network. Wait up to an hour.";
    if (e.status === 401) {
      return "That passkey was not accepted. It may have been deleted in TradePrizm; sign in with your password instead.";
    }
    return e.message;
  }
  return webauthnErrorMessage(e);
}

export type LoginApi = Pick<typeof api, "meta" | "passwordSignIn" | "pinSignIn" | "requestMagicLink">;
/** The code and reset steps' endpoints (in `accountApi`, outside the entry chunk). */
export type LoginStepApi = Pick<
  typeof accountApi,
  "verifySignInCode" | "resendSignInCode" | "forgotPassword" | "resetPassword"
>;

type Step =
  { kind: "password" } | { kind: "code"; challenge: string; resendAfterSecs: number } | { kind: "forgot" };

// The code step and "Forgot password" come after the first screen: their own chunks, outside the
// entry budget (audit S-12). The service worker precaches them.
const CodeStep = lazyComponent(() => import("./CodeStep").then((m) => m.CodeStep), "sign-in code");
const ResetPasswordForm = lazyComponent(
  () => import("./ResetPasswordForm").then((m) => m.ResetPasswordForm),
  "password reset",
);

/**
 * Sign-in (SPEC §17.6 step 1, §20, ADR-0051). Email + password; on a device HELM has not seen, a
 * six-digit code is emailed and "Remember this device" skips it for 30 days. A passkey signs in
 * directly where passkeys work at this address. "Forgot password" resets it with an emailed code.
 * The PIN (Personal mode) and the email link stay under "Other ways to sign in".
 */
export function LoginScreen({
  passkeysAvailable,
  pinAvailable,
  initialEmail = "",
  initialNotice = null,
  loginApi = api,
  stepApi,
  onSignedIn = () => {
    location.reload();
  },
}: {
  /** Overrides for the dev preview; normally read from `/v1/meta` and the browser. */
  passkeysAvailable?: boolean;
  pinAvailable?: boolean;
  initialEmail?: string;
  initialNotice?: string | null;
  loginApi?: LoginApi;
  /** Dev preview only; the steps default to `accountApi`. */
  stepApi?: LoginStepApi;
  onSignedIn?: () => void;
}): React.JSX.Element {
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(initialNotice);
  const [passkeys, setPasskeys] = useState(passkeysAvailable ?? false);
  const [pinOffered, setPinOffered] = useState(pinAvailable ?? false);
  const [emailCodes, setEmailCodes] = useState(true);
  const [sendFailures, setSendFailures] = useState(0);
  const [retryAt, setRetryAt] = useState(0);
  const retryIn = useSecondsLeft(retryAt);

  useEffect(() => {
    loginApi
      .meta()
      .then((m) => {
        // `passkeys_here`: a passkey is bound to the address it was made on (ADR-0035).
        if (passkeysAvailable === undefined) {
          setPasskeys(webauthnSupported() && (m.passkeys_here ?? m.passkeys) === true);
        }
        if (pinAvailable === undefined) setPinOffered(m.pin_sign_in === true);
        setEmailCodes(m.email_codes !== false);
      })
      .catch(() => {
        /* the password form still works */
      });
  }, [passkeysAvailable, pinAvailable, loginApi]);

  /** Run a request with the busy flag and one error line; a failed send starts the backoff. */
  const run = (work: () => Promise<void>, describe: (e: unknown) => string): void => {
    setBusy(true);
    setError(null);
    setNotice(null);
    work()
      .then(() => {
        setSendFailures(0);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === "EMAIL_SEND_FAILED") {
          setSendFailures(sendFailures + 1);
          setRetryAt(Date.now() + sendRetryDelayMs(sendFailures + 1));
        }
        setError(describe(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const signIn = (): void => {
    run(async () => {
      const r = await loginApi.passwordSignIn(email.trim(), password);
      if (r.status === "signed_in") {
        setPassword("");
        onSignedIn();
        return;
      }
      setStep({ kind: "code", challenge: r.challenge, resendAfterSecs: r.resend_after_secs });
    }, passwordSignInError);
  };

  const signInWithPin = (): void => {
    run(
      () =>
        loginApi.pinSignIn(email.trim(), pin).then(() => {
          setPin("");
          onSignedIn();
        }),
      (e) => {
        setPin("");
        return pinSignInError(e);
      },
    );
  };

  const back = (): void => {
    setStep({ kind: "password" });
    setError(null);
  };

  if (step.kind === "code") {
    return (
      <div className="login">
        <CodeStep
          email={email.trim()}
          challenge={step.challenge}
          resendAfterSecs={step.resendAfterSecs}
          {...(stepApi ? { codeApi: stepApi } : {})}
          onSignedIn={() => {
            setPassword("");
            onSignedIn();
          }}
          onBack={back}
        />
      </div>
    );
  }
  if (step.kind === "forgot") {
    return (
      <div className="login">
        <ResetPasswordForm
          initialEmail={email}
          {...(stepApi ? { resetApi: stepApi } : {})}
          onDone={() => {
            setPassword("");
            back();
            setNotice("Password changed. Sign in with the new one.");
          }}
          onBack={back}
        />
      </div>
    );
  }
  const retryLabel = retryIn > 0 ? ` (${String(retryIn)} s)` : "";
  return (
    <div className="login">
      <form
        className="login-card"
        onSubmit={(e) => {
          e.preventDefault();
          signIn();
        }}
      >
        <Brand as="div" className="login-brand" />
        {passkeys && (
          <>
            <button
              type="button"
              className="btn login-passkey"
              onClick={() => {
                run(() => signInWithPasskey().then(onSignedIn), passkeyError);
              }}
              disabled={busy}
            >
              {busy ? "Waiting…" : "Sign in with passkey"}
            </button>
            <div className="login-or" aria-hidden="true">
              <span>or use your email and password</span>
            </div>
          </>
        )}
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            data-testid="email-input"
            onChange={(ev) => {
              setEmail(ev.target.value);
            }}
            disabled={busy}
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            data-testid="password-input"
            onChange={(ev) => {
              setPassword(ev.target.value);
            }}
            disabled={busy}
          />
        </label>
        <button
          type="submit"
          className="btn btn-primary"
          data-testid="signin-submit"
          disabled={busy || retryIn > 0 || email.trim() === "" || password === ""}
        >
          {busy ? "Signing in…" : `Sign in${retryLabel}`}
        </button>
        {emailCodes ? (
          <div className="login-links">
            <button
              type="button"
              className="btn btn-link"
              onClick={() => {
                setStep({ kind: "forgot" });
                setError(null);
              }}
            >
              Forgot password?
            </button>
          </div>
        ) : (
          <p className="muted login-hint">
            This server cannot send email yet, so a new device cannot get a sign-in code. Use a passkey
            {pinOffered ? " or your PIN" : ""}, or a device TradePrizm remembers.
          </p>
        )}
        {notice && (
          <p className="login-sent" role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}
        <details className="login-other">
          <summary>Other ways to sign in</summary>
          {pinOffered && (
            <div className="login-other-row">
              <label className="field">
                <span>PIN</span>
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="[0-9]*"
                  maxLength={PIN_MAX_LENGTH}
                  value={pin}
                  data-testid="pin-input"
                  onChange={(ev) => {
                    setPin(ev.target.value.replace(/\D/g, ""));
                  }}
                  disabled={busy}
                />
              </label>
              <button
                type="button"
                className="btn"
                disabled={busy || pin === "" || email.trim() === ""}
                onClick={signInWithPin}
              >
                Sign in with PIN
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn"
            disabled={busy || email.trim() === ""}
            onClick={() => {
              run(async () => {
                await loginApi.requestMagicLink(email.trim());
                setNotice(
                  "If that address is allowed, a sign-in link was sent. In development it is printed in the helm-api log.",
                );
              }, magicLinkError);
            }}
          >
            Send a sign-in link
          </button>
        </details>
      </form>
    </div>
  );
}
